type Locale = {
  code: string
  name: string
}

type LocaleContent = {
  [key: string]: string
}

/**
 * This is the list of all locales that we have available in the system.
 * It's a copy of the list from backend and should be kept up-to-date with it:
 * https://github.com/Attensi/attensi-backend/blob/891ddad854ac52fd8246c749929f7f75a6b5a0f0/config/initializers/load_locales.rb#L5
 * Our own copy is needed to set name aliases so we have more control over them.
 */
export const contentLocales: Locale[] = [
  { code: 'ar', name: 'Arabic' },
  { code: 'ar-SA', name: 'Arabic (Saudi Arabia)' },
  { code: 'bg-BG', name: 'Bulgarian' },
  { code: 'bn-BD', name: 'Bangla (Bangladesh)' },
  { code: 'bn-IN', name: 'Bangla (India)' },
  { code: 'bs-BA', name: 'Bosnian' },
  { code: 'cs-CZ', name: 'Czech' },
  { code: 'da-DK', name: 'Danish' },
  { code: 'de-DE', name: 'German' },
  { code: 'el-GR', name: 'Greek' },
  { code: 'en-AU', name: 'English (Australia)' },
  { code: 'en-CA', name: 'English (Canada)' },
  { code: 'en-GB', name: 'English UK' },
  { code: 'en-US', name: 'English US' },
  { code: 'es-ES', name: 'Spanish (Spain)' },
  { code: 'es-CO', name: 'Espanol (Latin America)' },
  { code: 'et-EE', name: 'Estonian' },
  { code: 'fa-AF', name: 'Dari' },
  { code: 'fi-FI', name: 'Finnish' },
  { code: 'fr-BE', name: 'French (Belgium)' },
  { code: 'fr-CA', name: 'French (Canada)' },
  { code: 'fr-FR', name: 'French (France)' },
  { code: 'hi-IN', name: 'Hindi' },
  { code: 'hu-HU', name: 'Hungarian' },
  { code: 'hr-HR', name: 'Croatian' },
  { code: 'id-ID', name: 'Indonesian' },
  { code: 'it-IT', name: 'Italian' },
  { code: 'ja-JP', name: 'Japanese' },
  { code: 'km-KH', name: 'Khmer' },
  { code: 'ko-KR', name: 'Korean' },
  { code: 'lo-LA', name: 'Lao' },
  { code: 'lt-LT', name: 'Lithuanian' },
  { code: 'lv-LV', name: 'Latvian' },
  { code: 'ms-MY', name: 'Malay' },
  { code: 'my-MM', name: 'မြန်မာဘာသာ (Myanmar)' },
  { code: 'nb-NO', name: 'Norwegian' },
  { code: 'nl-BE', name: 'Flemish' },
  { code: 'nl-NL', name: 'Dutch' },
  { code: 'nn-NO', name: 'Norwegian Nynorsk' },
  { code: 'pl-PL', name: 'Polish' },
  { code: 'ps-AF', name: 'Pashto' },
  { code: 'pt-PT', name: 'Portuguese (Portugal)' },
  { code: 'pt-BR', name: 'Portuguese (Brazil)' },
  { code: 'ro-RO', name: 'Romanian' },
  { code: 'ru-RU', name: 'Russian' },
  { code: 'sk-SK', name: 'Slovak' },
  { code: 'sl-SI', name: 'Slovenian' },
  { code: 'sr-Latn-RS', name: 'Serbian (Latin)' },
  { code: 'sv-SE', name: 'Swedish' },
  { code: 'ta-IN', name: 'Tamil' },
  { code: 'th-TH', name: 'Thai' },
  { code: 'tl-PH', name: 'Tagalog' },
  { code: 'tr-TR', name: 'Turkish' },
  { code: 'uk-UA', name: 'Ukrainian' },
  { code: 'ur-IN', name: 'Urdu (India)' },
  { code: 'ur-PK', name: 'Urdu (Pakistan)' },
  { code: 'vi-VN', name: 'Vietnamese' },
  { code: 'zh-CN', name: 'Mandarin - Chinese (Simplified)' },
  { code: 'zh-HK', name: 'Cantonese - Cantonese (Traditional)' },
  { code: 'zh-TW', name: 'Chinese (Traditional)' },
  { code: 'kk-KZ', name: 'Kazakh' },
  { code: 'ne-NP', name: 'Nepali' },
  { code: 'cnh', name: 'Hakha Chin (Myanmar)' },
  { code: 'sw-KE', name: 'Swahili' },
]

export const systemLocales: Locale[] = [
  { code: 'en-US', name: 'English' },
  { code: 'nb-NO', name: 'Norwegian' },
  { code: 'de-DE', name: 'German' },
  { code: 'sv-SE', name: 'Swedish' },
]

/**
 * formattedLocale
 * @param {String} localeOldFormat - the old format: 'nb-NO'
 * @returns {String} locale in new format: 'nb_no'
 */
export const formattedLocale = (localeOldFormat: string) => {
  return localeOldFormat?.replace('-', '_').toLowerCase()
}

const getLocaleStorageContentLocaleKey = (companyName: string) => {
  return `attensi_admin:${companyName}:content_locale`
}

export const setLocaleStorageContentLocale = (
  currentCompany: string,
  localeCode: string
) => {
  const localStorageKey = getLocaleStorageContentLocaleKey(currentCompany)
  localStorage.setItem(localStorageKey, localeCode)
}

export const getLocalStorageContentLocale = (currentCompany: string) => {
  const localStorageKey = getLocaleStorageContentLocaleKey(currentCompany)
  return localStorage.getItem(localStorageKey)
}

export const getCorrectLocaleName = (locale: Locale) => {
  const foundLocale = contentLocales.find(
    (contentLocale) => locale.code === contentLocale.code
  )
  const localeName = foundLocale?.name ?? locale.name
  return localeName
}

export const getListOfLocalesWithContent = (
  content: LocaleContent,
  locales: { name: string; code: string }[]
) => {
  return locales
    .filter(
      (locale) => Object.hasOwn(content, locale.code) && content[locale.code]
    )
    .map((locale) => ({
      ...locale,
      text: content[locale.code],
    }))
}
